import React, { useContext, useEffect, useMemo } from 'react';
import { withRouter } from 'react-router-dom';
import _ from 'lodash';
import { connect } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import moment from 'moment';
import { customStyle } from 'helper';
import { ModalContext } from 'App';
import useFormValidator from 'useFormValidator';
import DateInputPicker from 'modules/common/components/DateInputPicker2';
import Modal from 'modules/common/components/Modal';
import withForm from 'modules/common/hoc/withForm';
import * as actions from '../../../action';
import * as c from '../../../constant';

const years = _.range(1970, +moment().format('YYYY') + 1).sort((a, b) => b - a);

const yearsList = years.map((year) => ({
  label: year,
  value: year,
}));

const conditionList = [
  { label: 'NEW', value: 'NEW' },
  { label: 'SECONDHAND', value: 'SECOND HAND' },
];

const rules = {
  machine_id: 'required',
  date_acquired: 'required',
  condition: 'required',
  economic_life_estimated: 'required',
  actual_use_id: 'required',
  original_cost: 'required',
};

function UpdateModal({
  id,
  applicationId,
  defaultValues,
  updateMachineryAppraisal,
  isLoading,
  formData,
  setFormData,
  getMachineryAppraisalList,
  getKindOfMachinery,
  kindOfMachineryList,
  getActualUseList,
  actualUseList,
}) {
  const setModal = useContext(ModalContext);
  const validateForm = useFormValidator(formData, rules);

  const machineryOptions = kindOfMachineryList.map((x) => ({
    label: _.get(x, 'attributes.name'),
    value: x.id,
    id: x.id,
  }));

  const actualUseOptions = actualUseList.map((x) => ({
    label: _.get(x, 'attributes.name'),
    value: x.id,
    id: x.id,
  }));

  const rcn = useMemo(
    () =>
      _.get(formData, 'original_cost') !== '' &&
      _.get(formData, 'original_cost') !== undefined
        ? +_.get(formData, 'original_cost') * 1.2
        : 0,
    [_.get(formData, 'original_cost')]
  );

  const totalDepreciationPercentage = useMemo(
    () =>
      _.get(formData, 'rate_of_depreciation') !== '' &&
      _.get(formData, 'rate_of_depreciation') !== undefined &&
      _.get(formData, 'economic_life_estimated') !== '' &&
      _.get(formData, 'economic_life_estimated') !== undefined
        ? (+_.get(formData, 'rate_of_depreciation') / 100) *
          +_.get(formData, 'economic_life_estimated')
        : 0,
    [
      _.get(formData, 'rate_of_depreciation'),
      _.get(formData, 'economic_life_estimated'),
    ]
  );

  const totalDepreciationValue = useMemo(
    () => rcn * totalDepreciationPercentage,
    [rcn, totalDepreciationPercentage]
  );

  const depreciationValue = useMemo(
    () => rcn - totalDepreciationValue,
    [rcn, totalDepreciationValue]
  );

  const handleSelected = (key) => (e) => {
    setFormData({
      ...formData,
      [key]: e.value,
    });
  };

  const handleOnChange = (key) => (e) => {
    return setFormData({
      ...formData,
      [key]: e.target.value,
    });
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();
    const args = {
      ...formData,
      date_acquired: `${moment(_.get(formData, 'date_acquired')).format(
        'YYYY-MM-DD HH:mm'
      )}:00`,
    };
    updateMachineryAppraisal(id, applicationId, args, () => {
      setModal({ isOpen: false });
      getMachineryAppraisalList(id);
    });
  };

  const closeModal = (e) => {
    e.preventDefault();
    setModal({ isOpen: false });
  };

  useEffect(() => {
    getKindOfMachinery();
  }, []);

  useEffect(() => {
    getActualUseList();
  }, []);

  useEffect(() => {
    return () => {
      setModal({ isOpen: false });
    };
  }, []);

  useEffect(() => {
    if (!_.isEmpty(defaultValues)) {
      setFormData(
        _.pick(defaultValues, _.keys(c.MACHINERY_APPRAISAL_FORM_DATA))
      );
    }
  }, []);

  return (
    <Modal.Body>
      <form onSubmit={handleOnSubmit}>
        <div className="row">
          <div className="col-6 form-group">
            <label htmlFor="machine_id" className="form-label mb-0">
              Kind of Machinery &nbsp;{' '}
              {validateForm.renderMessage('machine_id')}
            </label>
            <Select
              options={machineryOptions}
              styles={{
                ...customStyle,
                control: (base) => ({
                  ...customStyle.control(base),
                  height: 37,
                  minHeight: 37,
                }),
              }}
              placeholder="-Select-"
              value={machineryOptions.find(
                (item) => item.value === _.get(formData, 'machine_id')
              )}
              onChange={handleSelected('machine_id')}
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="brand_and_model" className="form-label mb-0">
              Brand and Model
            </label>
            <input
              className="form-control"
              type="text"
              name="brand_and_model"
              onChange={handleOnChange('brand_and_model')}
              value={_.get(formData, 'brand_and_model') || ''}
              placeholder="Enter Brand and Model"
            />
          </div>
          <div className="col-6 form-group">
            <label
              htmlFor="capacity"
              className="form-label mb-0"
              style={{ paddingBottom: '0.20rem' }}
            >
              Capacity
            </label>
            <input
              className="form-control"
              type="text"
              name="capacity"
              onChange={handleOnChange('capacity')}
              value={_.get(formData, 'capacity') || ''}
              placeholder="Enter Capacity"
            />
          </div>
          <div className="form-group col-6">
            <label htmlFor="date_acquired" className="form-label mb-0">
              Date Acquired &nbsp; {validateForm.renderMessage('date_acquired')}
            </label>
            <DateInputPicker
              onChange={handleOnChange('date_acquired')}
              value={_.get(formData, 'date_acquired') || ''}
              maxDate={new Date()}
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="condition" className="form-label mb-0">
              Condition &nbsp; {validateForm.renderMessage('condition')}
            </label>
            <Select
              options={conditionList}
              styles={{
                ...customStyle,
                control: (base) => ({
                  ...customStyle.control(base),
                  height: 37,
                  minHeight: 37,
                }),
              }}
              placeholder="-Select-"
              value={conditionList.find(
                (item) => item.value === _.get(formData, 'condition')
              )}
              onChange={handleSelected('condition')}
            />
          </div>
          <div className="col-6 form-group">
            <label
              htmlFor="economic_life_estimated"
              className="form-label mb-0"
            >
              Economic Life Estimated &nbsp;{' '}
              {validateForm.renderMessage('economic_life_estimated')}
            </label>
            <input
              className="form-control"
              type="number"
              name="economic_life_estimated"
              autoComplete="none"
              min="1"
              onChange={handleOnChange('economic_life_estimated')}
              value={_.get(formData, 'economic_life_estimated') || ''}
            />
          </div>
          <div className="col-6 form-group">
            <label
              htmlFor="economic_life_remaining"
              className="form-label mb-0"
            >
              Economic Life Remaining
            </label>
            <input
              className="form-control"
              type="number"
              name="economic_life_remaining"
              autoComplete="none"
              min="1"
              onChange={handleOnChange('economic_life_remaining')}
              value={_.get(formData, 'economic_life_remaining') || ''}
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="year_installed" className="form-label mb-0">
              Year Installed
            </label>
            <Select
              options={yearsList}
              styles={{
                ...customStyle,
                control: (base) => ({
                  ...customStyle.control(base),
                  height: 37,
                  minHeight: 37,
                }),
              }}
              placeholder="Select Year"
              value={yearsList.find(
                (item) => item.value === _.get(formData, 'year_installed')
              )}
              onChange={handleSelected('year_installed')}
            />
          </div>
          <div className="col-6 form-group">
            <label
              htmlFor="year_of_initial_operation"
              className="form-label mb-0"
            >
              Year of Initial Operation
            </label>
            <Select
              options={yearsList}
              styles={{
                ...customStyle,
                control: (base) => ({
                  ...customStyle.control(base),
                  height: 37,
                  minHeight: 37,
                }),
              }}
              placeholder="Select Year"
              value={yearsList.find(
                (item) =>
                  item.value === _.get(formData, 'year_of_initial_operation')
              )}
              onChange={handleSelected('year_of_initial_operation')}
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="actual_use_id" className="form-label mb-0">
              Actual Use &nbsp; {validateForm.renderMessage('actual_use_id')}
            </label>
            <Select
              options={actualUseOptions}
              styles={{
                ...customStyle,
                control: (base) => ({
                  ...customStyle.control(base),
                  height: 37,
                  minHeight: 37,
                }),
              }}
              placeholder="-Select-"
              value={actualUseOptions.find(
                (item) => item.value === _.get(formData, 'actual_use_id')
              )}
              onChange={handleSelected('actual_use_id')}
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="no_of_year_used" className="form-label mb-0">
              No. of Years Used
            </label>
            <input
              className="form-control"
              type="number"
              name="no_of_year_used"
              onChange={handleOnChange('no_of_year_used')}
              value={_.get(formData, 'no_of_year_used') || ''}
              min="0"
            />
          </div>
          <div className="col-6 form-group">
            <label htmlFor="original_cost" className="form-label mb-0">
              Original Cost &nbsp; {validateForm.renderMessage('original_cost')}
            </label>
            <input
              className="form-control"
              type="number"
              name="original_cost"
              onChange={handleOnChange('original_cost')}
              value={_.get(formData, 'original_cost') || ''}
            />
          </div>
          <div className="col-12 form-group mt-2">
            <table className="css-table table table-borderless table-sm table-striped">
              <tbody>
                {/* <tr>
                  <td className="history-color">Conversion Factor</td>
                  <td className="history-color">1.12</td>
                </tr> */}
                <tr>
                  <td className="history-color">RCN</td>
                  <td className="history-color">{`${rcn}`}</td>
                </tr>
                <tr className="w-100">
                  <td className="history-color">Depreciation Rate</td>
                  <td colSpan="6" className="history-color">
                    <div>
                      <input
                        className="form-control"
                        type="number"
                        name="rate_of_depreciation"
                        onChange={handleOnChange('rate_of_depreciation')}
                        value={_.get(formData, 'rate_of_depreciation') || ''}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td className="history-color">Total Depreciation %</td>
                  <td className="history-color">
                    {`${totalDepreciationPercentage}%`}
                  </td>
                </tr>
                <tr>
                  <td className="history-color">Total Depreciation Value</td>
                  <td className="history-color">{`PHP ${totalDepreciationValue}`}</td>
                </tr>
                <tr>
                  <td className="history-color">Depreciation Value</td>
                  <td className="history-color">{`PHP ${depreciationValue}`}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="d-flex justify-content-center">
          <button
            type="submit"
            className="mt-3 btn btn-primary cms-bg-color-primary cms-outline-primary  mr-3"
            disabled={!validateForm.response.is_passed || isLoading}
          >
            <span className="font-weight-bold">Submit</span>
          </button>
          <button
            type="button"
            onClick={closeModal}
            className="mt-3 btn btn-dark "
            disabled={isLoading}
          >
            <span className="font-weight-bold">Cancel</span>
          </button>
        </div>
      </form>
    </Modal.Body>
  );
}

UpdateModal.propTypes = {
  id: PropTypes.string.isRequired,
  applicationId: PropTypes.string.isRequired,
  defaultValues: PropTypes.instanceOf(Object).isRequired,
  getMachineryAppraisalList: PropTypes.instanceOf(Function).isRequired,
  updateMachineryAppraisal: PropTypes.instanceOf(Function).isRequired,
  isLoading: PropTypes.bool.isRequired,
  formData: PropTypes.instanceOf(Object).isRequired,
  setFormData: PropTypes.instanceOf(Function).isRequired,
  getKindOfMachinery: PropTypes.instanceOf(Function).isRequired,
  kindOfMachineryList: PropTypes.instanceOf(Array).isRequired,
  getActualUseList: PropTypes.instanceOf(Function).isRequired,
  actualUseList: PropTypes.instanceOf(Array).isRequired,
};

const mapStateToProps = ({ api }) => ({
  kindOfMachineryList: _.get(api, `${c.GET_LIST_OF_MACHINERY}.list`) || [],
  actualUseList: _.get(api, `${c.GET_ACTUAL_USE}.list`) || [],
  isLoading:
    _.get(api, 'loading', []).indexOf(c.UPDATE_MACHINERY_APPRAISAL) > -1,
});

const enhance = _.flowRight([
  withForm,
  withRouter,
  connect(mapStateToProps, {
    ...actions,
  }),
]);

export default enhance(UpdateModal);
